import connectClient from "./connectClient";
import { authService } from "../../../utils/auth";

const nameCache = new Map();

const directoryService = {
  cacheUser(userId, fullName) {
    if (userId && fullName && fullName !== userId) {
      nameCache.set(String(userId), fullName);
    }
  },

  getCachedName(userId) {
    if (!userId) return null;
    return nameCache.get(String(userId)) || null;
  },

  // GET /connect/directory/members?q=&limit=  -> [{ user_id, full_name, email, avatar }]
  async searchMembers(q, limit = 50) {
    try {
      const res = await connectClient.get("/connect/directory/members", {
        params: { q: q || "", limit: Math.min(limit, 200) },
      });
      if (Array.isArray(res.data) && res.data.length > 0) {
        res.data.forEach((m) => {
          if (m?.user_id && m?.full_name) {
            nameCache.set(String(m.user_id), m.full_name);
          }
        });
        return res.data;
      }
    } catch {
      /* ignore */
    }

    // Fallback to main backend /api/calendar/users/search if available
    try {
      // Single source of truth for the access token (cross-tab reconciled, cleared on logout).
      const token = authService.getToken();
      if (!token) return [];
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
      const resp = await fetch(`${backendUrl}/api/calendar/users/search?q=${encodeURIComponent(q || "")}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data.users)) {
          const list = data.users.map((u) => {
            const uid = String(u.user_id || u.id || u._id);
            const fullName = u.full_name || u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || uid;
            if (uid && fullName) {
              nameCache.set(uid, fullName);
            }
            return {
              user_id: uid,
              full_name: fullName,
              email: u.email,
              avatar: u.avatar || u.avatar_url,
            };
          });
          return list;
        }
      }
    } catch {
      /* ignore */
    }

    return [];
  },

  // POST /connect/directory/resolve-names -> { user_id: display_name }
  async resolveNames(userIds = []) {
    if (!userIds || userIds.length === 0) return {};
    const stringIds = userIds.map((u) => String(u)).filter(Boolean);
    const result = {};
    const missing = [];

    for (const uid of stringIds) {
      if (nameCache.has(uid)) {
        result[uid] = nameCache.get(uid);
      } else {
        missing.push(uid);
      }
    }

    if (missing.length === 0) {
      return result;
    }

    try {
      const res = await connectClient.post("/connect/directory/resolve-names", {
        user_ids: missing,
      });
      if (res.data && typeof res.data === "object") {
        Object.entries(res.data).forEach(([uid, name]) => {
          if (uid && name && name !== uid) {
            nameCache.set(String(uid), name);
            result[uid] = name;
          } else if (uid) {
            result[uid] = name;
          }
        });
      }
    } catch {
      /* ignore */
    }

    // If any IDs are still missing or unresolved (returning raw ID), try member search
    const stillRaw = missing.filter((uid) => !result[uid] || result[uid] === uid);
    if (stillRaw.length > 0) {
      try {
        const members = await this.searchMembers("", 100);
        members.forEach((m) => {
          if (m?.user_id && m?.full_name) {
            nameCache.set(String(m.user_id), m.full_name);
            if (stillRaw.includes(String(m.user_id))) {
              result[String(m.user_id)] = m.full_name;
            }
          }
        });
      } catch {
        /* ignore */
      }
    }

    return result;
  },
};

export default directoryService;
